import { useState } from "react";
import { Button, Pagination } from "react-bootstrap";
import DownloadPdfButton from "../../components/DownloadFileButton";
import InlinePageLink from "../../components/InlinePageLink";

export default function InvestmentPlanningGuide() {

    const NUM_OF_STEPS = 7
    const [active, setActive] = useState(1);

    return (

        <div>

            <p>Welcome to the Investment Planner Guide, your step-by-step guide to setting and reaching a financial goal through strategic investing. Whether you're saving for your child’s education, a major purchase, or another future expense, this tool will help you build a plan to get you there!
            </p>

            <h4 className="fs-5 mb-3 mt-3">How to Use This Tool:</h4>
            <ol>
                <li><strong>Define Your Goal</strong></li>
                <li><strong>Determine Your Horizon</strong></li>
                <li><strong>Assess Your Risk Tolerance</strong></li>
                <li><strong>Choose Your Assets</strong></li>
                <li><strong>Choose Your Investment Vehicle</strong></li>
                <li><strong>Determine Target Monthly Contribution</strong></li>
                <li><strong>Adjust the Plan as Needed</strong></li>
            </ol>

            <p>By following these steps, you’ll turn your financial aspirations into a concrete strategy and put your money to work for you.</p>

            <div className="d-flex justify-content-end">
                <DownloadPdfButton label='Download as PDF' url="/InvestmentPlanningGuide.pdf" type="pdf" />
            </div>

            <hr className="my-4" />

            <Pagination className="justify-content-center my-4">
                <Pagination.First onClick={() => setActive(1)} disabled={active === 1} />
                <Pagination.Prev onClick={() => setActive(active - 1)} disabled={active === 1} />
                {Array.from({ length: NUM_OF_STEPS }, (_, i) => (
                    <Pagination.Item key={i + 1} active={i + 1 === active} onClick={() => setActive(i + 1)}>
                        {i + 1}
                    </Pagination.Item>
                ))}
                <Pagination.Next onClick={() => setActive(active + 1)} disabled={active === NUM_OF_STEPS} />
                <Pagination.Last onClick={() => setActive(NUM_OF_STEPS)} disabled={active === NUM_OF_STEPS} />
            </Pagination>


            <div className="mx-1">

                <div hidden={active !== 1}>
                    <h3 id="Step1" className="fs-4 mb-4">Step 1: Define Your Goal</h3>

                    <p>Determine your investment goal: how much and for what purpose.</p>

                    <p className="fw-bold mt-4">Example</p>
                    <blockquote>
                        Save $50,000 for education for my child’s first two years of school.
                    </blockquote>
                </div>

                <div id="Step2" hidden={active !== 2}>

                    <h3 className="fs-4 mb-4">Step 2: Determine your Horizon</h3>

                    <p>Determine how long you have to contribute to your investment before you need to begin withdrawing.</p>

                    <p className="fw-bold mt-4">Example</p>
                    <blockquote>
                        Horizon: 15 years
                    </blockquote>

                </div>

                <div id="Step3" hidden={active !== 3}>

                    <h3 className="fs-4 mb-4">Step 3: Assess Your Risk Tolerance</h3>

                    <p><em>Note: A good question to ask here is what happens if my investment does not achieve it’s goal, how impactful would that be?</em></p>

                    <p className="fw-bold mt-4">Example</p>
                    <blockquote>
                        Risk: Medium/High
                    </blockquote>

                </div>

                <div id="Step4" hidden={active !== 4}>

                    <h3 className="fs-4 mb-4">Step 4: Choose you Assets</h3>

                    <p>Choose assets that meet your risk tolerance and timeline. </p>

                    <p className="fw-bold mt-4">Example</p>
                    <blockquote>
                        Assets: Stocks (10% expected return)
                    </blockquote>

                </div>

                <div id="Step5" hidden={active !== 5}>

                    <h3 className="fs-4 mb-4">Step 5: Choose Your Investment Vehicle</h3>

                    <p>Determine what investment vehicle (account type) to put your investments in.</p>

                    <p className="fw-bold mt-4">Example</p>
                    <blockquote>
                        Investment Vehicle: New Mexico 529
                    </blockquote>

                </div>

                <div id="Step6" hidden={active !== 6}>

                    <h3 className="fs-4 mb-4">Step 6: Determine Target Monthly Contribution</h3>

                    <p>Using a <InlinePageLink title={"Compound Interest Calculator"} />, determine how much you need to contribute monthly in order to meet your goal. Remember, this is just a target. Any fraction of the target will move you towards your goal.</p>
                    <p><em>Note: You will have to make an initial guess and adjust until the projected value meets your target.</em></p>

                    <p className="fw-bold mt-4">Example</p>
                    <blockquote>
                        Initial Values
                        <ul>
                            <li>Initial Investment: $0</li>
                            <li>Annual Interest Rate: 8%</li>
                            <li>Years: 15</li>
                            <li>Projected Value: $50,175.54</li>
                        </ul>
                        <br />
                        Monthly Contribution: $145
                    </blockquote>

                </div>

                <div id="Step7" hidden={active !== 7}>

                    <h3 className="fs-4 mb-4">Step 7: Adjust Plan</h3>

                    <p>Periodically revisit your plan and adjust as circumstances change.</p>

                    <blockquote>
                        <ul>
                            <li>Time Horizon Shortens ⇒ Lower risk</li>
                            <li>Investment Underperformance ⇒ Increase return potential through increasing risk</li>
                            <li>Income Increases ⇒ Raise monthly contribution to meet target contribution</li>
                        </ul>
                    </blockquote>
                </div>

            </div>

            <div className="d-flex flex-row justify-content-between my-5">
                <Button href={"#Step" + active} onClick={() => setActive(active - 1)} hidden={active === 1} className="btn-outline-theme">{"<  Back"}</Button>
                <div hidden={active !== 1} />
                <Button href={"#Step" + active} onClick={() => setActive(active + 1)} hidden={active === NUM_OF_STEPS} className="btn-outline-theme">{"Next  >"}</Button>
            </div>

        </div>
    )
}